import styled, { css, keyframes } from 'styled-components';
import { faBullseyeArrow, faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIsDarkMode } from 'src/common/hooks/use-is-dark-mode';
import { ToolTip } from 'src/common/components';
const getBorderColor = ($isDarkMode: boolean) => $isDarkMode ? 'rgba(62, 71, 90, 1)' : 'rgba(238, 239, 242, 1)';
const getAccentColor = ($isDarkMode: boolean) => $isDarkMode ? 'rgba(113, 156, 249, 1)' : 'rgba(17, 83, 228, 1)';
const growAnimation = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.5); }
  100% { transform: scale(1); }
`;
const checkGrowAnimation = keyframes`
  0% { transform: scale(0); }
  30% { transform: scale(0.5); }
  100% { transform: scale(1); }
`;
const rippleAnimation = ($isDarkMode: boolean) => keyframes`
  0%{
    opacity: 0;
    border: 0.3rem solid ${getAccentColor($isDarkMode)};
    transform: scale(1);
  }

  35% {
    opacity: 1;
    transform: scale(1.5);
  }

  100% {
    transform: scale(1);
    border-color: transparent;
  }
`;
const iconWrapperGrowAnimation = ($isDarkMode: boolean) => keyframes`
  0% { transform: scale(1); border: .1rem solid ${getBorderColor($isDarkMode)} }
  15% {transform: scale(1.3);border: .1rem solid ${getAccentColor($isDarkMode)}}
  30% {transform: scale(1);border: .1rem solid ${getBorderColor($isDarkMode)}}
  100% { transform: scale(1); border: .1rem solid ${getBorderColor($isDarkMode)}}
`;
const Container = styled.div<{
  $isCorrect: boolean;
}>`
  position: relative;
  width: 3.8rem;
  height: 3.8rem;
  min-height: 3.8rem;
  min-width: 3.8rem;
`;
const Effect = styled.div<{
  $isCorrect: boolean;
  $isDarkMode: boolean;
  $isFirstNonAnsweredQuestion: boolean;
}>`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background-color: transparent;
  animation: ${({
  $isCorrect,
  $isDarkMode,
  $isFirstNonAnsweredQuestion
}) => $isCorrect || !$isFirstNonAnsweredQuestion ? 'none' : rippleAnimation($isDarkMode)}
    2s infinite ease;
  z-index: 0;
`;
const IconWrapper = styled.div<{
  $isCorrect: boolean;
  $isDarkMode: boolean;
  $isFirstNonAnsweredQuestion: boolean;
}>`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: ${({
  theme,
  $isCorrect
}) => $isCorrect ? 'rgba(163, 234, 42, 1)' : theme.colors.background.main};
  border: ${({
  $isCorrect,
  $isDarkMode
}) => !$isCorrect && `0.136rem solid ${getBorderColor($isDarkMode)}`};
  animation: ${({
  $isCorrect,
  $isDarkMode,
  $isFirstNonAnsweredQuestion
}) => {
  if ($isCorrect) {
    return css`
        ${growAnimation} 1.2s ease
      `;
  }
  if ($isFirstNonAnsweredQuestion) {
    return css`
        ${iconWrapperGrowAnimation($isDarkMode)} infinite 2s ease
      `;
  }
  return css`none`;
}};
  z-index: 1;
`;
const Icon = styled.div<{
  $isCorrect: boolean;
  $isDarkMode: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 1.5rem;
  width: 1.5rem;
  color: ${({
  $isCorrect,
  $isDarkMode
}) => $isCorrect ? 'rgba(21, 28, 43, 1)' : getAccentColor($isDarkMode)};
  animation: ${({
  $isCorrect
}) => $isCorrect ? checkGrowAnimation : 'none'} 1.2s ease-in-out;
  transition: transform 1.2s ease-in-out;
  z-index: 2;
`;
export const AutoAnswerIcon = ({
  correct,
  isFirstNonAnsweredQuestion
}: {
  correct: boolean;
  isFirstNonAnsweredQuestion: boolean;
}) => {
  const isDarkMode = useIsDarkMode();
  return <Container $isCorrect={correct} data-sentry-element="Container" data-sentry-component="AutoAnswerIcon" data-sentry-source-file="auto-answer-icon.tsx">
      <Effect $isCorrect={correct} $isDarkMode={isDarkMode} $isFirstNonAnsweredQuestion={isFirstNonAnsweredQuestion} data-sentry-element="Effect" data-sentry-source-file="auto-answer-icon.tsx" />
      <IconWrapper $isCorrect={correct} $isDarkMode={isDarkMode} $isFirstNonAnsweredQuestion={isFirstNonAnsweredQuestion} data-sentry-element="IconWrapper" data-sentry-source-file="auto-answer-icon.tsx">
        {correct ? <Icon $isCorrect={correct} $isDarkMode={isDarkMode}>
            <FontAwesomeIcon icon={faCheck} />
          </Icon> : <ToolTip toolTipText="Execute the required action in the virtual machine to finish the task" $fontSize="tiny" $contentMaxWidth="25rem" $textAlign="center" disableHoverableContent>
            <Icon $isCorrect={correct} $isDarkMode={isDarkMode}>
              <FontAwesomeIcon icon={faBullseyeArrow} />
            </Icon>
          </ToolTip>}
      </IconWrapper>
    </Container>;
};